import Cookies from 'js-cookie'
import React from 'react'

import {identify} from '@helios/shared/utils/analytics'

// After the user is inactive for this amount of time, we consider them to be in a new session
const SESSION_LENGTH_IN_MINUTES = 30

// The name of the cookie key that gets set to track the user's landing page per session
const LANDING_PAGE_KEY = 'session:landingPage'
const RAW_LANDING_PAGE_KEY = 'session:rawLandingPage'
const LANDING_LOCALE = 'session:landingLocale'

type Args = {
    pathname: string
}

/**
 * This hook adds traits to the user which get fresh values after
 * `SESSION_LENGTH_IN_MINUTES` of inactivity (when the user is considered to be
 * in a new session).
 *
 * @param args - The hook arguments
 * @param args.pathname - The pathname of the current page
 */
export const useSessionTracking = (args: Args) => {
    React.useEffect(() => {
        const storedLandingPage = Cookies.get(LANDING_PAGE_KEY)
        const storedRawLandingPage = Cookies.get(RAW_LANDING_PAGE_KEY)
        const storedLandingLocale = Cookies.get(LANDING_LOCALE)

        const [locale, ...rawPathParts] = args.pathname.replace(/^\//, '').split('/')
        const rawPath = rawPathParts.join('/')

        // If the cookie exists and hasn't expired, keep the same value.
        // Otherwise, use the path of the current page
        const landingPage = storedLandingPage || args.pathname
        const rawLandingPage = storedRawLandingPage || rawPath
        const landingLocale = storedLandingLocale || locale

        const expires = new Date()

        // Set the cookie expiration as `SESSION_LENGTH_IN_MINUTES` from now
        expires.setMinutes(expires.getMinutes() + SESSION_LENGTH_IN_MINUTES)

        const cookiesToSet = [
            {key: LANDING_PAGE_KEY, value: landingPage},
            {key: RAW_LANDING_PAGE_KEY, value: rawLandingPage},
            {key: LANDING_LOCALE, value: landingLocale},
        ]

        cookiesToSet.forEach(({key, value}) => {
            Cookies.set(key, value, {
                // Reset the session timer on every page load
                expires,
            })
        })

        identify({
            landingPage,
            rawLandingPage,
            landingLocale,
        })
    }, [args.pathname])
}
